import { Align, TypographyAlign } from "../components/typography"
import {
    Background,
    BackgroundColor,
    Container,
    ContainerType,
    Spacer,
    SpacerVariant,
    spacerOptionsAll,
} from "../components/layout"
import React, { useEffect, useMemo, useState } from "react"
import { Repeater, types, useAdminContext, usePageValues } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../RedesignTypography"
import { useAPIState, useSDK } from "../../shared/context/client"

import { EqualHeight } from "react-equal-height/clean"
import { MatrixRedesignItemProps } from "./matrix/Item"
import classNames from "classnames"
import { useLocalesState } from "../../shared/context/locales"
import { usePageContext } from "../../context/page"
import { useQuery } from "@tanstack/react-query"

export interface ProductMatrixRedesignProps {
    repeater?: {
        id: string
        props: MatrixRedesignItemProps
        type: "MatrixRedesignItem"
    }[]
    sku?: string
    spacerTop?: SpacerVariant
    spacerBottom?: SpacerVariant
    headline: TypographyElement[]
    usp: TypographyElement[]
    id?: string
    index?: number
}

const ProductMatrixRedesign: types.Brick<ProductMatrixRedesignProps> = ({
    repeater,
    spacerTop,
    spacerBottom,
    id,
    sku,
    index,
}) => {
    const { region } = useLocalesState()
    const { products: productsBuildTime, page: pageContext } = usePageContext()

    const client = useSDK()
    const cart_id = useAPIState(s => s.cart_id)
    const cart_access_token = useAPIState(s => s.cart_access_token)

    const [pageValues] = usePageValues()

    const page = pageContext || pageValues

    const { data } = useQuery({
        queryKey: ["products", ...(repeater?.map(r => r.props.sku).filter(r => r) || []), region, page.language],
        queryFn: ({ signal }) =>
            client.getProducts(
                {
                    sku: repeater?.map(r => r.props.sku).filter((r): r is string => !!r),
                    country: region,
                    cart_id: cart_id,
                    access_token: cart_access_token,
                },
                page.language,
                signal
            ),
        enabled: !!repeater?.length,
        meta: { background: true },
    })

    const products = data || productsBuildTime

    const [activeTab, setActiveTab] = useState(repeater?.findIndex(r => r.props.sku === sku) || 0)

    const changeTab = (sku?: string) => {
        if (!repeater || !sku) return
        setActiveTab(repeater.findIndex(r => r.props.sku === sku))
    }

    const { isAdmin } = useAdminContext()

    useEffect(() => {
        if (!products || !repeater) return

        setActiveTab(
            repeater.findIndex(
                r =>
                    !r.props.sku || (products.map(p => p.sku).includes(r.props.sku) && sku ? r.props.sku === sku : true)
            ) || 0
        )
    }, [products, repeater, sku])

    const repeaterLengthFiltered = useMemo(
        () =>
            repeater?.filter(r => !(r.props.sku && !products?.find(p => p.sku === r.props.sku) && !isAdmin)).length ||
            0,
        [repeater, products, isAdmin]
    )

    return (
        <div key={index}>
            <Background color={BackgroundColor.White} className="sm:overflow-hidden">
                <Container type={ContainerType.Redesign}>
                    <div id={id}>
                        <Spacer variant={spacerTop} />
                        <Align align={TypographyAlign["Center + mobile left"]}>
                            <Typography propName="headline" allowList={["S1"]} />
                        </Align>
                        <ul
                            className={classNames(
                                "mb-16 grid w-full grid-cols-1 gap-10 sm:mb-10 sm:grid-cols-3 lg:mx-auto xl:mb-16",
                                { "lg:w-fit": !isAdmin }
                            )}
                        >
                            <Repeater propName="topRepeater" />
                        </ul>
                        <div className="sm:mt-5.5 sm:rounded-4xl sm:border sm:border-border-tide sm:px-14 sm:py-7 md:px-[72px] md:pb-10 md:pt-14 lg:border-0 lg:px-0 lg:py-0">
                            <EqualHeight updateOnChange={[repeaterLengthFiltered, activeTab]}>
                                <ul
                                    className={classNames(
                                        "-mx-2.5 grid gap-1.5 pt-5 xs:-mx-5 sm:mx-0 sm:gap-2 lg:gap-3.5 xl:gap-5",
                                        repeaterLengthFiltered % 3 === 0
                                            ? "grid-cols-3 lg:grid-cols-6"
                                            : "grid-cols-[repeat(2,1fr)] lg:grid-cols-6"
                                    )}
                                >
                                    <Repeater
                                        propName="repeater"
                                        itemProps={
                                            {
                                                products,
                                                repeaterLength: repeater?.length || 0,
                                                repeaterLengthFiltered,
                                                changeTab,
                                                activeTab,
                                                firstOnMobile: sku,
                                            } as MatrixRedesignItemProps
                                        }
                                    />
                                </ul>
                                <ul className="-mx-2.5 xs:mx-0 lg:hidden">
                                    <Repeater
                                        propName="repeater"
                                        itemProps={
                                            {
                                                products,
                                                repeaterLengthFiltered,
                                                changeTab,
                                                activeTab,
                                                showActiveOnly: true,
                                            } as MatrixRedesignItemProps
                                        }
                                    />
                                </ul>
                            </EqualHeight>
                        </div>
                    </div>
                    <Spacer variant={spacerBottom} />
                </Container>
            </Background>
        </div>
    )
}

ProductMatrixRedesign.schema = {
    name: "ProductMatrixRedesign",
    label: "Product Matrix Redesign",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/ProductMatrixRedesign.png`,
    repeaterItems: [
        {
            name: "topRepeater",
            items: [
                {
                    type: "MatrixRedesignUsp",
                    label: "USP",
                    min: 3,
                },
            ],
        },
        {
            name: "repeater",
            items: [
                {
                    type: "MatrixRedesignItem",
                    label: "Product",
                    min: 2,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "sku",
            label: "first Product Mobile",
            type: types.SideEditPropType.Text,
        },
        {
            name: "id",
            label: "ID",
            type: types.SideEditPropType.Text,
        },
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
            ],
        },
    ],
    getDefaultProps: (): ProductMatrixRedesignProps => ({
        spacerTop: SpacerVariant.XL,
        spacerBottom: SpacerVariant.XL,
        headline: [
            {
                type: "h2",
                children: [
                    {
                        text: "Lorem ipsum",
                        S1: true,
                    },
                ],
            },
        ],
        usp: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum",
                        bold: true,
                    },
                    {
                        text: "\nLorem ipsum dolor sit amet, consetetur sadipscing elitr",
                    },
                ],
            },
        ],
    }),
}

export default ProductMatrixRedesign
